import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Watershed } from '../models/watershed.model';
import { ReportForest } from '../models/reportForest.model';

@Injectable({
    providedIn: 'root'
})
export class ReportService {
    reportRelations = '&relations=users,users.skills,users.user,users.user.skills,users.user.skills.skill,assessment,supporting_files,supports,soil_burn_severities,comments,forests,forests.treatments,report_status,stateCounties,watersheds';

    constructor(private readonly httpClient: HttpClient) { }

    getReportList(assessment_id: number, reportRelations: string): Observable<any> {
        let params = new HttpParams()
        let queryParams = `?assessment_id=${assessment_id}`
        return this.setNotStartedStatus(this.httpClient.get(
            `${environment.apiUrl}/report${queryParams}${reportRelations}`, { params: params }
        ));
    }
    getReportListByUser(reportRelations: string, user_id: number): Observable<any> {
        let params = new HttpParams()
        let queryParams = `?report_member_user_ids=${user_id}`
        return this.httpClient.get(
            `${environment.apiUrl}/report${queryParams}${reportRelations}`, { params: params }
        );
    }
    getReportListByStatus(reportRelations: string, report_status_id: number | null, authorization_status_id: number | null): Observable<any> {
        let params = new HttpParams()
        let queryParams = ''
        if (authorization_status_id == null) {
            queryParams = `?report_status_id=${report_status_id}`
        } else {
            queryParams = `?authorization_status_id=${authorization_status_id}`
            queryParams += `&report_status_id=${report_status_id}`
        }
        return this.httpClient.get(
            `${environment.apiUrl}/report${queryParams}${reportRelations}`, { params: params }
        );
    }
    getReport(report_id: number, relations?: string[]): Observable<any> {
        let params = new HttpParams()
        let queryParams = `?report_id=${report_id}`

        if (relations && relations.length > 0) {
            queryParams += "&relations=" + relations.join(",");
        }

        return this.setNotStartedStatus(this.httpClient.get(
            `${environment.apiUrl}/report${queryParams}`, { params: params }
        ));
    }

    createReport(assessment: any, report_num: number): Observable<any> {
        let data = {
            assessment_id: assessment.assessment_id,
            report_status_id: 0, // In Progress
            report_num: report_num,
        };
        let params = new HttpParams()
        return this.httpClient.post(
            `${environment.apiUrl}/report`, data, { params: params }
        );
    }

    createReportEverything(report: any): Observable<any> {
        let params = new HttpParams()
        return this.httpClient.post(
            `${environment.apiUrl}/report`, report, { params: params }
        );
    }

    updateReport(data: {
        report_id: number,
        report_status_id?: number,
        authorization_status_id?: number,
        supporting_files?: any[],
        report_num?: number,

        submission_date?: string,
        supports?: any,

        // report type
        report_type?: string,
        initial_report_type?: string,

        // burned area
        watersheds?: Watershed[],
        baer_name?: string,
        IRWIN_unique_fire_identifier?: number,
        job_code?: string,
        vegetation_types?: string,
        dominant_soils?: string,
        geologic_types?: string,
        other_roads?: string,

        fire_discovery_date?: any,
        containment_date_est?: any,
        containment_date?: any,
        percent_contained?: any,

        //watersheds
        soil_burn_severities?: any[],
        erosion_hazard?: string,
        burn_severity_description?: string,
        erosion_potential?: string,
        debris_potential?: string,
        vegetative_recovery?: string,
        hydrolic_response?: string,

        // summary of analysis
        background?: string,
        threats_human?: string,
        threatsproperty?: string,
        threats_natural?: string,
        threats_cultural?: string,
        threats_emergency?: string,
        treat_prob_land?: number | null,
        treat_prob_channel?: number,
        treat_prob_trails?: number,
        treat_prob_safety?: number,
        critical_values?: string,
        cost_no_action?: string,
        cost_alternative?: string,

        //recommended treatments
        forests?: ReportForest[],

        //emergency stabilzation
        assessment_cost?: string,

        //team supporting
        supporting_documents?: string,
        recommendations_map?: string,
        burn_severity_map?: string,
        kmz_submittal_package?: string,

        page_1_status_id?: number,
        page_2_status_id?: number,
        page_3_status_id?: number,
        page_4_status_id?: number,
        page_5_status_id?: number,
        page_6_status_id?: number,
        page_7_status_id?: number,
        page_8_status_id?: number,
        regional_funding_status_id?: number,
        regional_approval_status_id?: number,
        wo_funding_status_id?: number,
        wo_approval_status_id?: number,

        // Many more options to be added
    }): Observable<any> {
        let params = new HttpParams()
        return this.httpClient.put(
            `${environment.apiUrl}/report`, data, { params: params }
        );
    }

    getReportComments(report_id: number): Observable<any> {
        let params = new HttpParams()
        let queryParams = `?report_id=${report_id}`
        return this.httpClient.get(
            `${environment.apiUrl}/report-comment${queryParams}`, { params: params }
        );
    }

    createReportComment(data: any): Observable<any> {
        let params = new HttpParams();
        return this.httpClient.post(
            `${environment.apiUrl}/report-comment`, data, { params: params }
        );
    }

    editReportComment(data: any, comment_id: any): Observable<any> {
        let params = new HttpParams();
        return this.httpClient.put(
            `${environment.apiUrl}/report-comment/${comment_id}`, data, { params: params }
        );
    }

    deleteReportComment(comment_id: any): Observable<any> {
        let params = new HttpParams();
        return this.httpClient.delete(
            `${environment.apiUrl}/report-comment/${comment_id}`, { params: params, responseType: "blob" as any },
        );
    }

    getReportPageList() {
        return [
            { pageNum: 1, pageName: 'Report Type' },
            { pageNum: 2, pageName: 'Burned Area Description' },
            { pageNum: 3, pageName: 'Watershed Condition' },
            { pageNum: 4, pageName: 'Summary of Analysis' },
            { pageNum: 5, pageName: 'Recommended Treatment Narratives' },
            { pageNum: 6, pageName: 'Emergency Stabilization Treatments & Requested Funds' },
            { pageNum: 7, pageName: 'Team & Supporting Documents' },
            { pageNum: 8, pageName: 'Select Forest Unit Line Officer(s)' }
        ]
    }

    /**
   * Sends request for support for report, updates report with data
   *
   * @param data body of request
   * @returns Promise indicating success/failure
   */
    requestSupport(data: {
        report_id: any,
        support_needed_type?: string,
        support_skills?: string,
        support_details?: string,
        other_skill?: string,
    }): Observable<any> {
        let body = {
            report_id: data.report_id,
            supports: [{
                support_type: data.support_needed_type,
                support_skills: data.support_skills,
                additional_details: data.support_details,
                support_completed: false,
                other_skill: data.other_skill,
            }],
        }
        let params = new HttpParams()
        return this.httpClient.put(
            `${environment.apiUrl}/report`, body, { params: params }
        );
    }

    createBurnSeverity(data: {
        report_id: number,
        ownership: string,
        acres_burned: number,
        percent_burned: number,
        burn_acres_developed: number,
        burn_acres_unburned: number,
        burn_acres_low: number,
        burn_acres_moderate: number,
        burn_acres_high: number,
    }): Observable<any> {
        let params = new HttpParams()
        return this.httpClient.post(
            `${environment.apiUrl}/report-soil-burn-severity`, data, { params: params }
        );
    }

    editBurnSeverity(report_soil_burn_severity_id: number, data: {
        report_id: number,
        ownership: string,
        acres_burned: number,
        percent_burned: number,
        burn_acres_developed: number,
        burn_acres_unburned: number,
        burn_acres_low: number,
        burn_acres_moderate: number,
        burn_acres_high: number,
    }): Observable<any> {
        let params = new HttpParams()
        return this.httpClient.put(
            `${environment.apiUrl}/report-soil-burn-severity/` + report_soil_burn_severity_id, data, { params: params }
        );
    }

    uploadFile(data: any): Observable<any> {
        let params = new HttpParams()
        return this.httpClient.post(
            `${environment.apiUrl}/file`, data, { params: params },
        );
    }

    getThumbnail(supporting_files_id: number): Observable<any> {
        let params = new HttpParams()
        return this.httpClient.get(
            `${environment.apiUrl}/file/${supporting_files_id}/thumbnail`, { params: params, responseType: "blob" as any },
        );
    }

    getFullSizeImage(supporting_files_id: number): Observable<any> {
        let params = new HttpParams()
        return this.httpClient.get(
            `${environment.apiUrl}/file/${supporting_files_id}`, { params: params, responseType: "blob" as any },
        );
    }

    deleteFile(supporting_files_id: number): Observable<any> {
        let params = new HttpParams()
        return this.httpClient.delete(
            `${environment.apiUrl}/file/${supporting_files_id}`, { params: params, responseType: "blob" as any },
        );
    }

    updateFile(supporting_files_id: number, data: any): Observable<any> {
        let params = new HttpParams()
        return this.httpClient.put(
            `${environment.apiUrl}/file/${supporting_files_id}`, data, { params: params },
        );
    }

    getTreatmentOptions(): Observable<any> {
        let params = new HttpParams()
        return this.httpClient.get(
            `${environment.apiUrl}/metadata/treatment-options`, { params: params },
        );
    }

    getReportStatuses(): Observable<any> {
        let params = new HttpParams()
        return this.httpClient.get(
            `${environment.apiUrl}/metadata/report-statuses`, { params: params },
        );
    }

    setNotStartedHelper(report: any) {
        if (report.assessment?.assessment_status_id == 3) { // In Progress
            let startDate = new Date(report.assessment.tentative_start_week)
            if (new Date() < startDate) {
                report.report_status_id = -10
                report.report_status = {
                    report_status: "Not Started",
                    report_status_id: -10,
                }
            }
        }
    }

    setNotStartedStatus(result: Observable<any>): Observable<any> {
        let subject = new Subject<any>()
        result.subscribe({
            next: (response) => {
                if (response.reports) {
                    response.reports.forEach(this.setNotStartedHelper)
                }
                subject.next(response)
            },
            error: (response) => {
                subject.error(response)
            },
            complete: () => {
                subject.complete()
            }
        })
        return subject
    }

    createWaterShed(report_id: number): Observable<any> {
        let data = {
            report_id: report_id
        }
        let params = new HttpParams()
        return this.httpClient.post(
            `${environment.apiUrl}/watershed`, data, { params: params },
        );
    }

    deleteWatershed(watershed_id: number): Observable<any> {
        let params = new HttpParams()
        return this.httpClient.delete(
            `${environment.apiUrl}/watershed/${watershed_id}`, { params: params },
        );
    }

    deleteFireTreatment(treatment_id: any): Observable<any> {
        let params = new HttpParams();
        return this.httpClient.delete(
            `${environment.apiUrl}/forest-treatment/${treatment_id}`, { params: params },
        );
    }

    createFireTreatment(data: any): Observable<any> {
        let params = new HttpParams();
        return this.httpClient.post(
            `${environment.apiUrl}/forest-treatment`, data, { params: params },
        );
    }

    deleteSpec(spec_id: any): Observable<any> {
        let params = new HttpParams();
        return this.httpClient.delete(
            `${environment.apiUrl}/treatment-specification/${spec_id}`, { params: params },
        );
    }

    createSpec(data: any): Observable<any> {
        let params = new HttpParams();
        return this.httpClient.post(
            `${environment.apiUrl}/treatment-specification`, data, { params: params },
        );
    }

    createReportSignature(data: any): Observable<any> {
        let params = new HttpParams();
        return this.httpClient.post(
            `${environment.apiUrl}/report-signature`, data, { params: params },
        );
    }

    editReportSignature(data: any, report_signature_id: string): Observable<any> {
        let params = new HttpParams();
        return this.httpClient.put(
            `${environment.apiUrl}/report-signature/${report_signature_id}`, data, { params: params },
        );
    }

    deleteSignature(report_signature_id: number): Observable<any> {
        let params = new HttpParams();
        return this.httpClient.delete(
            `${environment.apiUrl}/report-signature/${report_signature_id}`, { params: params },
        );
    }

    getAuthorizedByOptions(): Observable<any> {
        let params = new HttpParams()
        return this.httpClient.get(
            `${environment.apiUrl}/metadata/authorization-methods`, { params: params },
        );
    }

    getSpecAuthorizedStatusOptions(): Observable<any> {
        let params = new HttpParams()
        return this.httpClient.get(
            `${environment.apiUrl}/metadata/spec-authorization-statuses`, { params: params },
        );
    }

    editSpec(data: any, spec_id: any): Observable<any> {
        let params = new HttpParams();
        return this.httpClient.put(
            `${environment.apiUrl}/treatment-specification/${spec_id}`, data, { params: params }
        );
    }

    deleteReport(report_id_list: number[]): Observable<any> {
        let body = {
            report_id_list,
        }
        let params = new HttpParams()
        return this.httpClient.delete(
            `${environment.apiUrl}/report`, { params, body },
        );
    }

    notifyGIS(report_id: number): Observable<any> {
        let params = new HttpParams();
        let data = {
            "report_id": report_id,
            "procedure": "notify_gis_specialist"
        }
        return this.httpClient.put(
            `${environment.apiUrl}/report`, data, { params: params }
        );
    }

    getStateList() {
        return ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']
    }
}
